import { useState } from 'react';
import { authService } from '../services/api';
import { useNotification } from '../components/Notifications/NotificationContext';

/**
 * Hook para gerenciar uploads de mídia do bot
 * Centraliza a lógica de upload e anexação de arquivos
 */
export function useMediaUpload() {
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const { showSuccess, showError } = useNotification();

  const uploadAndAttach = async ({ botId, targetType = 'bot', targetId = botId, file, slot, mediaType, onStatusChange, uploadContext = 'generic' }) => {
    if (!file) return null;

    try {
      setUploadingMedia(true);
      setUploadError(null);

      onStatusChange?.({
        phase: 'uploading',
        mediaType,
        fileName: file.name,
      });

      // ✅ Upload do arquivo - PASSA botId
      const uploadResponse = await authService.uploads.upload_media(botId, file, uploadContext);
      const uploadedFile = uploadResponse.data;

      // ✅ Anexar mídia ao bot
      await authService.uploads.attach_media({
        upload_id: uploadedFile.id,
        target_type: targetType,
        target_id: targetId,
        slot,
        media_type: mediaType,
      });

      onStatusChange?.({
        phase: 'media',
        mediaType,
        fileName: file.name,
        upload: uploadedFile,
      });

      return { success: true, upload: uploadedFile };
    } catch (err) {
      const error = err.response?.data?.error || err.message || 'Erro ao enviar mídia';
      setUploadError(error);
      showError(error);
      return { success: false, error };
    } finally {
      setUploadingMedia(false);
    }
  };

  const uploadBotMedia = async (botId, { welcomeMedia, welcomeAudio }) => {
    try {
      const uploads = [];

      if (welcomeMedia) {
        uploads.push(
          uploadAndAttach({
            botId,
            file: welcomeMedia,
            slot: 'welcome',
            mediaType: 'image_or_video',
          }).then((result) => {
            if (result.success) {
              showSuccess("Mídia de boas-vindas enviada!");
            }
            return result;
          })
        );
      }

      if (welcomeAudio) {
        uploads.push(
          uploadAndAttach({
            botId,
            file: welcomeAudio,
            slot: 'welcome_audio',
            mediaType: 'audio',
          }).then((result) => {
            if (result.success) {
              showSuccess("Áudio de boas-vindas enviado!");
            }
            return result;
          })
        );
      }

      const results = await Promise.all(uploads);
      const hasErrors = results.some(r => !r.success);

      return { success: !hasErrors };
    } catch (err) {
      const error = err.message || 'Erro ao enviar mídias';
      setUploadError(error);
      return { success: false, error };
    }
  };

  return {
    uploadBotMedia,
    uploadAndAttach,
    uploadingMedia,
    uploadError,
    clearError: () => setUploadError(null),
  };
}